// src/utils/formatters.js
import { format as formatDateFns, formatDistanceToNowStrict } from 'date-fns'

/**
 * Normalizes various timestamp formats (Firestore, object, string, Date) into a JS Date object.
 * Returns null if the input is invalid or missing.
 * @param {*} ts The timestamp value to normalize.
 * @returns {Date | null} A valid Date object or null.
 */
export function normalizeTimestamp(ts) {
  if (!ts) return null
  let date
  if (ts.toDate) {
    // Firestore Timestamp
    date = ts.toDate()
  } else if (typeof ts.seconds === 'number') {
    // Serialized Firestore Timestamp
    date = new Date(ts.seconds * 1000)
  } else {
    // JS Date, ISO string, or milliseconds
    date = new Date(ts)
  }
  return !isNaN(date.getTime()) ? date : null
}

/**
 * Formats a date value into a user-friendly string (e.g., "MMM d, yyyy").
 * @param {*} dateValue The date to format.
 * @returns {string} The formatted date string or 'N/A'.
 */
export function formatSimpleDate(dateValue) {
  const date = normalizeTimestamp(dateValue)
  if (!date) return 'N/A'
  return formatDateFns(date, 'MMM d, yyyy')
}

/**
 * Formats a date value into a detailed string with time (e.g., "MMM d, yyyy 'at' h:mm a").
 * @param {*} dateValue The date to format.
 * @returns {string} The formatted date-time string or 'N/A'.
 */
export function formatDetailedTimestamp(dateValue) {
  const date = normalizeTimestamp(dateValue)
  if (!date) return 'N/A'
  return formatDateFns(date, "MMM d, yyyy 'at' h:mm a")
}

/**
 * Formats a date into a "time ago" string (e.g., "3 days ago").
 * @param {*} dateValue The date value to format.
 * @returns {string} The formatted string or 'N/A'.
 */
export function formatTimeAgo(dateValue) {
  const date = normalizeTimestamp(dateValue)
  if (!date) return 'N/A'
  return formatDistanceToNowStrict(date, { addSuffix: true })
}

/**
 * Formats a timestamp to show time if it's today, otherwise shows the date.
 * @param {*} dateValue The date value to format.
 * @returns {string} The formatted string (e.g., "2:30 PM" or "Jan 15") or '—'.
 */
export function formatShortTimestamp(dateValue) {
  const date = normalizeTimestamp(dateValue)
  if (!date) return '—'

  const now = new Date()
  const isToday = now.toDateString() === date.toDateString()

  if (isToday) {
    return formatDateFns(date, 'p') // e.g., "12:00 PM"
  }
  return formatDateFns(date, 'MMM d') // e.g., "Jan 15"
}

/**
 * Formats a timestamp to show only the time of day (e.g., "2:30 PM").
 * Useful for chat message timestamps.
 * @param {*} ts The timestamp value to format.
 * @returns {string} The formatted time string or ''.
 */
export function formatTimeOnly(ts) {
  const date = normalizeTimestamp(ts)
  if (!date) return ''
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

/**
 * Formats a timestamp as a chat-style day separator: "Today", "Yesterday", or "Mon, Jan 15".
 * @param {*} ts The timestamp value to format.
 * @returns {string} The formatted separator string or ''.
 */
export function formatDateSeparator(ts) {
  const date = normalizeTimestamp(ts)
  if (!date) return ''
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
}

/**
 * A payback period in years and months ("5 yrs, 7 mos"), never a decimal: no one reads 5.59 years.
 * Same wording and nearest-month rounding as the product engine's `paybackLabel` (gse-estimate2
 * lib/deal_layer.py `_payback_label`) and the `{est_paybackPeriod}` token customers already receive,
 * so a fallback reads like the engine's own label. Zero or less is 'Immediate'; the
 * in-browser engine's beyond-term string ("25+") keeps its plus. Anything else returns null so the
 * caller chooses its own empty state.
 */
export function formatPaybackPeriod(years) {
  if (typeof years === 'string' && /^\d+\+$/.test(years.trim())) return `${years.trim()} yrs`
  if (years === null || years === undefined || years === '') return null
  const n = Number(years)
  if (!isFinite(n)) return null
  if (n <= 0) return 'Immediate'
  let whole = Math.floor(n)
  let months = Math.round((n - whole) * 12)
  if (months === 12) {
    whole += 1
    months = 0
  }
  const parts = []
  if (whole) parts.push(`${whole} yr${whole === 1 ? '' : 's'}`)
  if (months) parts.push(`${months} mo${months === 1 ? '' : 's'}`)
  return parts.join(', ') || 'Immediate'
}

/** Humanize fractional days to "XD YH" (e.g., 117.134 -> "117D 3H"). */
export function humanizeDays(daysFloat) {
  if (daysFloat === null || daysFloat === undefined) return '—'
  const n = Number(daysFloat)
  if (!isFinite(n)) return '—'
  const totalMinutes = Math.max(0, Math.round(n * 24 * 60))
  const d = Math.floor(totalMinutes / (24 * 60))
  const h = Math.floor((totalMinutes % (24 * 60)) / 60)
  const parts = []
  if (d > 0) parts.push(`${d}D`)
  parts.push(`${h}H`)
  return parts.join(' ')
}

/**
 * A comprehensive value formatter for various data types used in reports and tables.
 * Null/empty/NaN → '—'
 * @param {*} value The value to format.
 * @param {string} type The type ('currency','rate','perWatt','percent','number','days','kW','panels','date','string').
 * @returns {string} The formatted string or '—'.
 */
export function formatValue(value, type = 'string') {
  if (value === null || typeof value === 'undefined' || value === 'Not Found' || value === '')
    return '—'
  // Guard against NaN
  if (typeof value === 'number' && !isFinite(value)) return '—'
  if (typeof value === 'string' && value.toLowerCase() === 'nan') return '—'

  try {
    switch (type) {
      case 'currency':
        return Number(value).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })
      case 'rate':
        return Number(value).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        })
      case 'perWatt':
        return Number(value).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 4,
        })
      case 'percent':
        return isFinite(Number(value)) ? `${Number(value).toFixed(2)}%` : '—'
      case 'number':
        return isFinite(Number(value)) ? Number(value).toLocaleString('en-US') : '—'
      case 'days':
        return humanizeDays(value)
      case 'kW':
        return isFinite(Number(value)) ? `${Number(value).toFixed(2)} kW` : '—'
      case 'panels':
        return isFinite(Number(value)) ? `${Math.round(Number(value))} panels` : '—'
      case 'date':
        return formatSimpleDate(value)
      default:
        return String(value)
    }
  } catch (e) {
    console.warn(`Could not format value "${value}" as type "${type}".`, e)
    return '—'
  }
}

/**
 * Normalizes a phone number string into E.164 format for consistent storage.
 * @param {string} phoneString The phone number to normalize.
 * @returns {string|null} The normalized phone number (e.g., +13107796428) or null if invalid.
 */
export function normalizePhoneNumber(phoneString) {
  if (!phoneString || typeof phoneString !== 'string') {
    return null
  }
  const digits = phoneString.replace(/\D/g, '')
  if (digits.length === 10) {
    return `+1${digits}`
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`
  }
  if (phoneString.startsWith('+') && digits.length > 10) {
    return `+${digits}`
  }
  return null
}

/**
 * Derives the last 10 digits from a normalized E.164 phone number.
 * Used for inbound call/SMS lookup fallback (contact.phoneLast10).
 * @param {string|null} normalizedPhone An E.164 string from normalizePhoneNumber(), e.g. "+13107796428".
 * @returns {string|null} Last 10 digits (e.g. "3107796428") or null if input is invalid.
 */
export function derivePhoneLast10(normalizedPhone) {
  if (!normalizedPhone || typeof normalizedPhone !== 'string') return null
  const digits = normalizedPhone.replace(/\D/g, '')
  return digits.length >= 10 ? digits.slice(-10) : null
}

/**
 * Normalizes an email address.
 * @param {string} emailString The email string.
 * @returns {string|null} The normalized email or null if invalid.
 */
export function normalizeEmail(emailString) {
  if (!emailString || typeof emailString !== 'string') return null
  const trimmed = emailString.trim().toLowerCase()
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(trimmed) ? trimmed : null
}

/**
 * Central function to format and validate a value based on a specified type.
 * Supports: 'text' (default), 'currency', 'email', 'phone', 'url'.
 * @param {string} value The raw input value.
 * @param {string} formatType The format to apply.
 * @returns {{isValid: boolean, formattedValue: string, errorMessage: string}}
 */
export function formatAndValidate(value, formatType) {
  const val = value === null || value === undefined ? '' : String(value)

  // If empty, return valid (unless strict required logic handles it elsewhere)
  if (!val.trim()) {
    return { isValid: true, formattedValue: '', errorMessage: '' }
  }

  switch (formatType) {
    case 'currency': {
      // Remove everything except numbers and dots
      const cleanVal = val.replace(/[^0-9.]/g, '')
      const numberVal = parseFloat(cleanVal)

      if (isNaN(numberVal)) {
        return { isValid: false, formattedValue: val, errorMessage: 'Invalid currency amount' }
      }

      // Format as USD: $1,234.56
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numberVal)

      return { isValid: true, formattedValue: formatted, errorMessage: '' }
    }

    case 'email': {
      const formatted = normalizeEmail(val)
      return {
        isValid: formatted !== null,
        formattedValue: formatted || val.trim(),
        errorMessage: formatted === null ? 'Invalid email format.' : '',
      }
    }

    case 'phone': {
      const formatted = normalizePhoneNumber(val)
      // If valid, format nicely for display: (555) 123-4567
      // normalizePhoneNumber returns E.164 (+15551234567), we can reformat that or return strict E.164.
      // For display consistency in text fields, let's format it.
      let displayValue = formatted || val.trim()

      if (formatted) {
        // Strip +1
        const tenDigits = formatted.replace(/^\+1/, '')
        if (tenDigits.length === 10) {
          displayValue = `(${tenDigits.slice(0, 3)}) ${tenDigits.slice(3, 6)}-${tenDigits.slice(6)}`
        }
      }

      return {
        isValid: formatted !== null,
        formattedValue: displayValue,
        errorMessage: formatted === null ? 'Invalid phone number format.' : '',
      }
    }

    case 'url': {
      const trimmed = val.trim()

      // Allow plain domains by auto-prefixing https://
      const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

      try {
        const u = new URL(withScheme)
        return { isValid: true, formattedValue: u.toString(), errorMessage: '' }
      } catch {
        return { isValid: false, formattedValue: trimmed, errorMessage: 'Invalid URL format.' }
      }
    }

    case 'text':
    default:
      return { isValid: true, formattedValue: val.trim(), errorMessage: '' }
  }
}

/**
 * Capitalizes the first letter of a string.
 * @param {string} s The input string.
 * @returns {string} The capitalized string.
 */
export function capitalizeFirstLetter(s) {
  if (!s || typeof s !== 'string') return ''
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Cleans a string by trimming whitespace and collapsing multiple spaces into one.
 * @param {*} input The value to clean.
 * @returns {string} The cleaned string.
 */
export function cleanString(input) {
  if (!input || typeof input !== 'string') return ''
  return input.trim().replace(/\s+/g, ' ')
}

/**
 * Formats a number as USD currency string.
 * @param {number|string} value The value to format.
 * @param {number} fractionDigits Number of decimal places (default: 2).
 * @returns {string} Formatted currency string or '—'.
 */
export function formatCurrency(value, fractionDigits = 2) {
  const num = Number(value)
  if (!isFinite(num)) return '—'
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

/**
 * Formats a phone number string for display: (555) 123-4567.
 * @param {string} phone The phone number to format.
 * @returns {string} Formatted phone string or the original input.
 */
export function formatPhone(phone) {
  if (!phone) return ''
  const cleaned = String(phone).replace(/\D/g, '').replace(/^1/, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return String(phone)
}

/**
 * Truncates a string to a maximum length, appending an ellipsis.
 * @param {string} text The text to truncate.
 * @param {number} maxLength Maximum character length (default: 50).
 * @returns {string} The truncated string.
 */
/**
 * Returns true if the customer's email is currently bouncing (hard bounce
 * or active soft cooldown). Mirrors `isCustomerEmailBlocked` in
 * `functions/utils/emailBounceState.js` — backend cooldown defaults to 24h
 * but for the read-only UI indicator we treat any recorded bounce state as
 * worth surfacing; a stale soft bounce that's already cooled down is still
 * useful context for the agent ("this address bounced yesterday").
 *
 * Per-address (Alternate Contacts P5): reads `contact.bounceStates[address]`.
 * When no address is given, defaults to the PRIMARY (`contact.email`) — so the
 * customer-header indicator reflects the primary, while the composer passes the
 * selected recipient to show that address's state.
 *
 * @param {Object} customer
 * @param {string} [address] — the address to check; defaults to the primary
 * @returns {{ bouncing: boolean, isHard: boolean, smtpCode: string|null, reason: string|null }}
 */
export function getCustomerEmailBounceState(customer, address) {
  const key = String(address || customer?.contact?.email || '')
    .trim()
    .toLowerCase()
  const state = key ? customer?.contact?.bounceStates?.[key] : null
  if (!state) return { bouncing: false, isHard: false, smtpCode: null, reason: null }
  return {
    bouncing: true,
    isHard: !!state.isHardBounce,
    smtpCode: state.smtpCode || null,
    reason: state.reason || null,
    bounceCount: state.bounceCount || 0,
  }
}

export function truncateText(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text || ''
  return text.slice(0, maxLength).trim() + '…'
}
